import { useEffect, useState } from 'react';
import { Button, Dialog, FormAlert, NativeSelect, formatCurrency, useToast } from '@mgdlead/ui';

import {
  useCampaigns,
  useDispensarMeta,
  useLinkCampaignMeta,
  useMetaSemVinculo,
} from '../../lib/api';
import { humanizeError } from '../../lib/errors';

/**
 * O aviso de "campanha da Meta sem vínculo" (pedido do Carlos, 08/10).
 *
 * Caso que o originou: o Heliton duplicou a campanha na Meta e só a antiga estava ligada ao
 * bloco — as entradas da cópia contavam, o gasto não, e o CPL real ficou abaixo do CPL da
 * Meta. Duplicar campanha é rotina de tráfego; sem aviso, isso volta toda semana.
 *
 * Faixa no topo de TODAS as telas (como a do número caído) enquanto houver pendência, e a
 * janela abre sozinha UMA vez por campanha nova (lembrado no aparelho). Sugerir não é decidir
 * (invariante 13): a sugestão vem marcada e explicada, quem toca em Vincular é a pessoa.
 */

const CHAVE_VISTAS = 'mgdlead:meta-sem-vinculo:vistas:';

function lerVistas(workspaceId: string): Set<string> {
  try {
    const bruto = localStorage.getItem(CHAVE_VISTAS + workspaceId);
    return new Set(bruto ? (JSON.parse(bruto) as string[]) : []);
  } catch {
    return new Set();
  }
}

function gravarVistas(workspaceId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(CHAVE_VISTAS + workspaceId, JSON.stringify([...ids].slice(-200)));
  } catch {
    // sem armazenamento: a janela só não abre sozinha de novo; a faixa continua
  }
}

export function MetaSemVinculoBanner({
  workspaceId,
  enabled,
}: {
  workspaceId: string;
  enabled: boolean;
}) {
  const pendentes = useMetaSemVinculo(workspaceId, enabled).data;
  const [aberto, setAberto] = useState(false);

  // Campanha que a pessoa ainda não viu → a janela abre sozinha, uma vez.
  useEffect(() => {
    if (!pendentes || pendentes.length === 0) return;
    const vistas = lerVistas(workspaceId);
    const novas = pendentes.filter((p) => !vistas.has(p.externalId));
    if (novas.length === 0) return;
    for (const p of novas) vistas.add(p.externalId);
    gravarVistas(workspaceId, vistas);
    setAberto(true);
  }, [pendentes, workspaceId]);

  if (!pendentes || pendentes.length === 0) return null;

  const [primeira] = pendentes;
  const titulo =
    pendentes.length === 1
      ? `A campanha “${primeira?.name ?? primeira?.externalId}” da Meta não está ligada a nenhum bloco`
      : `${pendentes.length} campanhas da Meta não estão ligadas a nenhum bloco`;

  return (
    <div role="alert" className="mb-4">
      <FormAlert tone="warning" title={titulo}>
        <span className="block">
          Sem o vínculo, o gasto dela fica fora do CPL — e o custo por entrada aparece menor do que
          é. Isso acontece quando a campanha é duplicada ou criada nova na Meta.
        </span>
        <button
          type="button"
          onClick={() => setAberto(true)}
          className="mt-2 inline-flex min-h-11 items-center font-medium underline underline-offset-2 md:min-h-0"
        >
          Vincular agora
        </button>
      </FormAlert>
      <MetaSemVinculoDialog
        workspaceId={workspaceId}
        open={aberto}
        onOpenChange={setAberto}
        pendentes={pendentes}
      />
    </div>
  );
}

type Pendente = NonNullable<ReturnType<typeof useMetaSemVinculo>['data']>[number];

function MetaSemVinculoDialog({
  workspaceId,
  open,
  onOpenChange,
  pendentes,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendentes: Pendente[];
}) {
  const blocos = useCampaigns(workspaceId).data?.campaigns ?? [];
  // A janela fecha sozinha quando a última pendência sai.
  useEffect(() => {
    if (open && pendentes.length === 0) onOpenChange(false);
  }, [open, pendentes.length, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Campanhas da Meta sem bloco"
      description="Ligue cada uma ao bloco de grupos para onde ela manda gente. O gasto passa a entrar no custo por entrada e no CPL real."
    >
      <ul className="flex flex-col gap-3">
        {pendentes.map((p) =>
          p.conjuntosSemVinculo.length > 0 ? (
            <ConjuntosPendentes
              key={p.externalId}
              workspaceId={workspaceId}
              item={p}
              blocos={blocos.map((b) => ({ value: b.id, label: b.name }))}
            />
          ) : (
            <ItemPendente
              key={p.externalId}
              workspaceId={workspaceId}
              item={p}
              blocos={blocos.map((b) => ({ value: b.id, label: b.name }))}
            />
          ),
        )}
      </ul>
    </Dialog>
  );
}

function ItemPendente({
  workspaceId,
  item,
  blocos,
}: {
  workspaceId: string;
  item: Pendente;
  blocos: { value: string; label: string }[];
}) {
  const { toast } = useToast();
  const vincular = useLinkCampaignMeta();
  const dispensar = useDispensarMeta();
  const [bloco, setBloco] = useState(item.sugestao?.campaignId ?? '');
  const ocupado = vincular.isPending || dispensar.isPending;

  const fatos = [
    item.effectiveStatus === 'ACTIVE' ? 'no ar' : item.effectiveStatus ? 'pausada' : null,
    item.gastoCentavos !== null && item.gastoCentavos > 0
      ? `gastou ${formatCurrency(item.gastoCentavos / 100, item.moeda ?? 'BRL')} em 7 dias`
      : null,
    item.cliques > 0
      ? `${item.cliques} ${item.cliques === 1 ? 'clique' : 'cliques'} no seu link`
      : 'nenhum clique no seu link ainda',
  ].filter(Boolean);

  const motivo =
    item.sugestao?.motivo === 'CLIQUES'
      ? `Sugerido: ${item.sugestao.cliques} ${item.sugestao.cliques === 1 ? 'clique dela foi' : 'cliques dela foram'} para “${item.sugestao.name}”.`
      : item.sugestao?.motivo === 'NOME'
        ? `Sugerido: parece cópia de “${item.sugestao.parecidaCom}”, que já está em “${item.sugestao.name}”.`
        : 'Escolha o bloco. Se ela não manda gente para os grupos, toque em “Não é de grupo”.';

  const onVincular = () =>
    vincular.mutate(
      {
        workspaceId,
        campaignId: bloco,
        externalIds: [item.externalId],
        source: item.sugestao?.campaignId === bloco ? 'SUGGESTED' : 'MANUAL',
      },
      {
        onSuccess: () => toast({ title: 'Campanha vinculada', tone: 'success' }),
        onError: (e) =>
          toast({ title: 'Não deu para vincular', description: humanizeError(e), tone: 'danger' }),
      },
    );

  const onDispensar = () =>
    dispensar.mutate(
      { workspaceId, externalId: item.externalId },
      {
        onError: (e) =>
          toast({ title: 'Não deu para dispensar', description: humanizeError(e), tone: 'danger' }),
      },
    );

  return (
    <li className="rounded-lg border border-border p-3">
      <p className="font-medium text-foreground">{item.name ?? `Campanha ${item.externalId}`}</p>
      <p className="text-sm text-muted-foreground">{fatos.join(' · ')}</p>
      <p className="mt-2 text-sm text-foreground">{motivo}</p>
      <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center">
        <NativeSelect
          aria-label={`Bloco para ${item.name ?? item.externalId}`}
          value={bloco}
          onChange={(e) => setBloco(e.target.value)}
          wrapperClassName="md:flex-1"
          options={[{ value: '', label: 'Escolha o bloco…' }, ...blocos]}
        />
        <Button
          onClick={onVincular}
          disabled={bloco === '' || ocupado}
          loading={vincular.isPending}
        >
          Vincular
        </Button>
        <Button variant="ghost" onClick={onDispensar} disabled={ocupado}>
          Não é de grupo
        </Button>
      </div>
    </li>
  );
}

/**
 * Campanha ABO ligada por conjunto em que parte dos conjuntos ativos ainda não tem bloco
 * (0041). Ligar a campanha inteira seria recusado (dupla contagem), então a escolha é por
 * conjunto — cada um com o seu bloco.
 */
function ConjuntosPendentes({
  workspaceId,
  item,
  blocos,
}: {
  workspaceId: string;
  item: Pendente;
  blocos: { value: string; label: string }[];
}) {
  const dispensar = useDispensarMeta();
  const { toast } = useToast();
  const n = item.conjuntosSemVinculo.length;
  return (
    <li className="rounded-lg border border-border p-3">
      <p className="font-medium text-foreground">{item.name ?? `Campanha ${item.externalId}`}</p>
      <p className="text-sm text-muted-foreground">
        Ligada por conjunto, mas{' '}
        {n === 1 ? 'falta 1 conjunto ativo' : `faltam ${n} conjuntos ativos`} — o gasto{' '}
        {n === 1 ? 'dele' : 'deles'} fica fora de todos os blocos.
      </p>
      <ul className="mt-3 flex flex-col gap-3">
        {item.conjuntosSemVinculo.map((c) => (
          <ConjuntoPendente
            key={c.externalId}
            workspaceId={workspaceId}
            conjunto={c}
            blocos={blocos}
          />
        ))}
      </ul>
      <Button
        variant="ghost"
        className="mt-2"
        disabled={dispensar.isPending}
        onClick={() =>
          dispensar.mutate(
            { workspaceId, externalId: item.externalId },
            {
              onError: (e) =>
                toast({
                  title: 'Não deu para dispensar',
                  description: humanizeError(e),
                  tone: 'danger',
                }),
            },
          )
        }
      >
        Não é de grupo
      </Button>
    </li>
  );
}

function ConjuntoPendente({
  workspaceId,
  conjunto,
  blocos,
}: {
  workspaceId: string;
  conjunto: Pendente['conjuntosSemVinculo'][number];
  blocos: { value: string; label: string }[];
}) {
  const { toast } = useToast();
  const vincular = useLinkCampaignMeta();
  const [bloco, setBloco] = useState('');
  const nome = conjunto.name ?? `Conjunto ${conjunto.externalId}`;
  return (
    <li className="flex flex-col gap-2 md:flex-row md:items-center">
      <span className="text-sm text-foreground md:flex-1">{nome}</span>
      <NativeSelect
        aria-label={`Bloco para ${nome}`}
        value={bloco}
        onChange={(e) => setBloco(e.target.value)}
        wrapperClassName="md:flex-1"
        options={[{ value: '', label: 'Escolha o bloco…' }, ...blocos]}
      />
      <Button
        disabled={bloco === '' || vincular.isPending}
        loading={vincular.isPending}
        onClick={() =>
          vincular.mutate(
            {
              workspaceId,
              campaignId: bloco,
              externalIds: [conjunto.externalId],
              source: 'MANUAL',
              level: 'ADSET',
            },
            {
              onSuccess: () => toast({ title: 'Conjunto vinculado', tone: 'success' }),
              onError: (e) =>
                toast({
                  title: 'Não deu para vincular',
                  description: humanizeError(e),
                  tone: 'danger',
                }),
            },
          )
        }
      >
        Vincular
      </Button>
    </li>
  );
}
